import { Fragment, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useToast } from 'use-toast-mui';
import CheckIcon from '@mui/icons-material/Check';
import PictureAsPdfOutlinedIcon from '@mui/icons-material/PictureAsPdfOutlined';

import CommonSelect from '../../../form-components/CommonSelect';
import CommonPagination from '../../../form-components/CommonPagination';
import {
  getMemberContractsByMemberId,
  getContractPdf,
  getContractCategories,
  getContractStatuses,
} from '../../../api/contract';
import ElectronicContractDetail from '../../storeTabs/electronicContract/ElectronicContractDetail';
import { PageWrapper } from './UserContractHistoryStyled';
import {
  ToolbarRow,
  PageSizeGroup,
  PageSizeLabel,
  ActionGroup,
  ActionButton,
  Table,
  TableHeader,
  TableRow,
  Cell,
  HeaderText,
  CellDivider,
  CheckBoxCell,
  DateText,
  NameText,
  CategoryText,
  RegisterText,
  PhoneText,
  DetailButton,
  StatusText,
} from '../../storeTabs/electronicContract/ElectronicContractStyled';

const COLUMNS = [
  { key: 'contractDate', label: '계약일', width: 120 },
  { key: 'name', label: '계약서 명', width: 240 },
  { key: 'category', label: '카테고리' },
  { key: 'registrationType', label: '등록유형' },
  { key: 'managerName', label: '담당자' },
  { key: 'memberName', label: '계약자' },
  { key: 'phone', label: '전화번호', width: 140 },
  { key: 'detail', label: '상세보기' },
  { key: 'status', label: '상태' },
];

const PAGE_SIZE_OPTIONS = [
  { value: 5, label: '5' },
  { value: 10, label: '10' },
  { value: 20, label: '20' },
  { value: 50, label: '50' },
];

const findLabel = (list, value) => list.find(item => item.value === value)?.label || value;

const UserContractHistory = ({ user, bigUser }) => {
  const { t } = useTranslation();
  const toast = useToast();
  const memberId = user?.memberId;

  const [searchParams, setSearchParams] = useSearchParams();
  const contractId = searchParams.get('contractId');

  const [pageSize, setPageSize] = useState(5);
  const [page, setPage] = useState(1);
  const [category, setCategory] = useState('');
  const [selectedIds, setSelectedIds] = useState([]);

  const buildQuery = () => {
    const params = new URLSearchParams({ page, limit: pageSize, sortOrder: 'DESCENDING' });
    if (category) params.set('category', category);
    return params.toString();
  };

  const { data } = useQuery({
    queryKey: ['memberContracts', memberId, category, page, pageSize],
    queryFn: () => getMemberContractsByMemberId(memberId, buildQuery(), bigUser.token),
    enabled: !!memberId && !!bigUser?.token,
  });

  const { data: categories = [] } = useQuery({
    queryKey: ['contractCategories'],
    queryFn: () => getContractCategories(bigUser.token),
    enabled: !!bigUser?.token,
    staleTime: 1000 * 60 * 30,
  });

  const { data: statuses = [] } = useQuery({
    queryKey: ['contractStatuses'],
    queryFn: () => getContractStatuses(bigUser.token),
    enabled: !!bigUser?.token,
    staleTime: 1000 * 60 * 30,
  });

  const rows = data?.content || [];

  const handlePageSizeChange = e => {
    setPageSize(e.target.value);
    setPage(1);
  };

  const handleCategoryChange = e => {
    setCategory(e.target.value);
    setPage(1);
  };

  const allChecked = rows.length > 0 && rows.every(row => selectedIds.includes(row.id));
  const toggleSelectAll = () => setSelectedIds(allChecked ? [] : rows.map(row => row.id));
  const toggleSelectOne = id =>
    setSelectedIds(prev => (prev.includes(id) ? prev.filter(sid => sid !== id) : [...prev, id]));

  const handleDownloadPdf = async () => {
    if (selectedIds.length === 0) return toast.error(t('PDF를 다운로드할 계약서를 선택해 주세요.'));

    for (const id of selectedIds) {
      const row = rows.find(r => r.id === id);
      try {
        const blob = await getContractPdf(id, bigUser.token);
        const url = window.URL.createObjectURL(blob);
        const anchor = document.createElement('a');
        anchor.href = url;
        anchor.download = row?.name ? `${row.name}_${row.memberName || ''}.pdf` : `contract-${id}.pdf`;
        anchor.click();
        window.URL.revokeObjectURL(url);
      } catch {
        toast.error(t('PDF 다운로드에 실패했습니다.'));
      }
    }
  };

  const handleDetail = row => {
    const next = new URLSearchParams(searchParams);
    next.set('contractId', row.id);
    setSearchParams(next);
  };

  const handleBackToList = () => {
    const next = new URLSearchParams(searchParams);
    next.delete('contractId');
    setSearchParams(next, { replace: true });
  };

  // 상세보기
  if (contractId) {
    return <ElectronicContractDetail contractId={contractId} onBack={handleBackToList} hideMemberInfo />;
  }

  // 목록
  return (
    <PageWrapper>
      <ToolbarRow>
        <PageSizeGroup>
          <PageSizeLabel>{t('페이지당 항목 수')}</PageSizeLabel>
          <CommonSelect value={pageSize} onChange={handlePageSizeChange} options={PAGE_SIZE_OPTIONS} width={100} />
        </PageSizeGroup>

        <ActionGroup>
          <CommonSelect
            value={category}
            onChange={handleCategoryChange}
            emptyLabel={t('전체')}
            options={categories}
            width={140}
            sx={{ '& .MuiOutlinedInput-root': { height: '48px' } }}
          />
          <ActionButton tone="danger" startIcon={<PictureAsPdfOutlinedIcon />} onClick={handleDownloadPdf}>
            {t('PDF 다운로드')}
          </ActionButton>
        </ActionGroup>
      </ToolbarRow>

      <Table>
        <TableHeader>
          <CheckBoxCell checked={allChecked} clickable onClick={toggleSelectAll}>
            {allChecked && <CheckIcon sx={{ fontSize: 14 }} />}
          </CheckBoxCell>
          {COLUMNS.map((col, idx) => (
            <Fragment key={col.key}>
              {idx > 0 && <CellDivider />}
              <Cell width={col.width}>
                <HeaderText>{t(col.label)}</HeaderText>
              </Cell>
            </Fragment>
          ))}
        </TableHeader>

        {rows.length === 0 && (
          <TableRow style={{ justifyContent: 'center', minHeight: '120px', color: '#A6A6A6', fontSize: '14px' }}>
            {t('계약 내역이 없습니다.')}
          </TableRow>
        )}

        {rows.map(row => {
          const isChecked = selectedIds.includes(row.id);
          const isCanceled = row.status === 'CANCELED';
          return (
            <TableRow key={row.id}>
              <CheckBoxCell checked={isChecked} clickable onClick={() => toggleSelectOne(row.id)}>
                {isChecked && <CheckIcon sx={{ fontSize: 14 }} />}
              </CheckBoxCell>
              <Cell width={120}>
                <DateText>{row.contractDate}</DateText>
              </Cell>
              <Cell width={240}>
                <NameText>{row.name}</NameText>
              </Cell>
              <Cell>
                <CategoryText>{findLabel(categories, row.category)}</CategoryText>
              </Cell>
              <Cell>
                <RegisterText>{row.registrationTypeLabel}</RegisterText>
              </Cell>
              <Cell>
                <RegisterText>{row.managerName}</RegisterText>
              </Cell>
              <Cell>
                <RegisterText>{row.memberName}</RegisterText>
              </Cell>
              <Cell width={140}>
                <PhoneText>{row.phone}</PhoneText>
              </Cell>
              <Cell>
                <DetailButton onClick={() => handleDetail(row)}>{t('상세보기')}</DetailButton>
              </Cell>
              <Cell>
                <StatusText canceled={isCanceled}>{findLabel(statuses, row.status)}</StatusText>
              </Cell>
            </TableRow>
          );
        })}
      </Table>

      <CommonPagination
        totalElements={data?.totalElements}
        limit={pageSize}
        page={page}
        onChange={(_, value) => setPage(value)}
      />
    </PageWrapper>
  );
};

export default UserContractHistory;
